import _ from 'lodash'
import { MutationTree } from 'vuex'
import { MenuState } from '@/modules/cms/modules/menu/store'
import MenuEntry from '@/modules/cms/modules/menu/model/MenuEntry'
import MenuEntryTranslation from '@/modules/cms/modules/menu/model/MenuEntryTranslation'
import { searchItemRecursive } from '@/modules/cms/modules/menu/store/getters'
import Slot from '../model/Slot';

export enum MutationTypes {
    SetMenuList = 'list/set',
    SetCurrentMenu = 'current/set',
    SetCurrentMenuItem = 'current/item/set',
    DeleteItemOfMenu = 'current/item/delete',
    SetCreatingItem = 'current/creating-item',
    UpdateMenuEntryField = 'current/item/field/update',
    CreateNewMenuItem = 'current/item/create-sub-item',
    SetCurrentLocale = 'current-locale/set',
    UpdateCurrentTranslationField = 'current-item/translation/field/update',
    SetNewItemInsertionPoint = 'new-item/insertion-point/set',
    AddItemToMenu = 'current/item/add',
    AddCurrentItemToMenu = 'ADD_CURRENT_ITEM_TO_MENU',
    ToggleItemSelected = 'TOGGLE_ITEM_SELECTED',
    UpdateMenuField = 'UPDATE_MENU_FIELD',
    SetSlotList = 'SET_SLOT_LIST',
    ResetCurrentMenuItem = 'RESET_CURRENT_MENU_ITEM',
}


function addItem (state: MenuState, payload: {parent: string, item: MenuEntry}) {
    const { parent, item } = payload
    // Si no existe el padre o es falso se inserta directamente en la raiz
    if (!parent) {
        state.currentMenu.items.push(item)

        return
    }

    const parentItem = searchItemRecursive(state.currentMenu, parent)
    if (!parentItem) {
        throw new Error(`Error al inserta un elemento, no existe el nodo con id ${parent}`)
    }

    parentItem.items.push(item)
}

const mutations: MutationTree<MenuState> = {
    [MutationTypes.SetMenuList]: (state, list: MenuEntry[]) => state.menuList = list,
    [MutationTypes.SetCurrentMenu]: (state, menu: MenuEntry) => state.currentMenu = menu,
    [MutationTypes.SetCurrentMenuItem]: (state, item: MenuEntry) => state.currentMenuItem = item,

    /**
     * Elimina un elemento del menu
     */
    [MutationTypes.DeleteItemOfMenu]: (state, id: string) => {
        state.currentMenu.deleteItem(id)

        // Si el menú borrado se encontraba seleccionado por el usuario, se elimina
        if (state.currentMenuItem.tempId === id) {
            state.currentMenuItem = new MenuEntry()
            state.itemSelected = false
        }
    },

    [MutationTypes.SetCreatingItem]: (state, creating: boolean) => state.creatingItem = creating,

    /**
     * Actualiza un campo del sub-elemento de menú actualmente seleccionado por
     * el usuario
     */
    [MutationTypes.UpdateMenuEntryField]: (state, payload: {path: string, value: any}) => {
        const { path, value } = payload

        // Se actualiza también el título de la traducción
        if (path === 'name') {
            state.currentMenuItem.translate(state.currentLocale).title = value
        }

        _.set(state.currentMenuItem, payload.path, payload.value)
    },

    /**
     * Crea un nuevo sub-item y lo inserta como elemento actual
     */
    [MutationTypes.CreateNewMenuItem]: (state) => {
        const item = new MenuEntry()
        // Por defecto, la traducción en el idioma español
        item.translations.push(new MenuEntryTranslation('es'))

        state.currentMenuItem = item
    },

    /**
     * Establece el localismo actualmente seleccionado por el usuario
     */
    [MutationTypes.SetCurrentLocale]: (state, locale: string) => state.currentLocale = locale,

    /**
     * Actualiza un campo de la traducción actualmente seleccionada por el usuario
     */
    [MutationTypes.UpdateCurrentTranslationField]: (state, payload: { path: string, value: any}) => {
        _.set(state.currentMenuItem.translate(state.currentLocale), payload.path, payload.value)
    },


    /**
     * Establece el identificador del nodo donde se insertará el nuevo elemento en creación
     */
    [MutationTypes.SetNewItemInsertionPoint]: (state, id: string) => state.newItemInsertionPoint = id,

    /**
     * Añade un nuevo elemento al menú, busca el padre de forma recursiva
     */
    [MutationTypes.AddItemToMenu]: (state, payload: {parent: string, item: MenuEntry}) => {
        addItem(state, payload)
    },

    /**
     * Añade un nuevo menu
     */
    [MutationTypes.AddCurrentItemToMenu]: (state, parent: string) => {
        addItem(state, { parent, item: state.currentMenuItem })
    },

    /**
     * Habilita o deshabilita la opción de elemento seleccionado
     */
    [MutationTypes.ToggleItemSelected]: (state, selected: boolean) => state.itemSelected = selected,

    /**
     * Actualiza un campo del menú principal actualmente seleccionado
     */
    [MutationTypes.UpdateMenuField]: (state, payload: {path: string, value: any}) => {
        _.set(state.currentMenu, payload.path, payload.value)
    },

    [MutationTypes.SetSlotList]: (state, slots: Slot[]) => {
        state.slots = slots
    },

    /**
     * Borra el elemento de menu actualmente en edición e inserta uno vacío
     */
    [MutationTypes.ResetCurrentMenuItem]: (state) => state.currentMenuItem = new MenuEntry(),
}

export default mutations
